import { types, Instance, flow } from 'mobx-state-tree';
import { Node, Edge } from '@xyflow/react';
import api from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import { ToastService } from '../services/ToastService';
import { deepCloneForMST, safeMergeToMST, cloneMSTNode, fromAPIToMST } from './utils/mstUtils';
import { analyticsService } from '../services/analytics.service';

// Mindmap node model
const MindmapNode = types.model('MindmapNode', {
  id: types.string,
  type: types.optional(types.string, 'workflow'),
  label: types.optional(types.string, ''),
  parentId: types.maybeNull(types.string),
  isRoot: types.optional(types.boolean, false),
  priority: types.optional(types.string, 'medium'),
  isNecessary: types.optional(types.boolean, false),
  // Track if this is optimistically created (not yet confirmed by server)
  isOptimistic: types.optional(types.boolean, false),
  // External links (URLs with names)
  links: types.optional(types.frozen(), []),
  // General notes/description
  notes: types.optional(types.string, ''),
  // Team assignments
  lead: types.optional(types.string, ''), // Single lead user ID
  supportMembers: types.optional(types.array(types.string), []), // Multiple support member user IDs
  followers: types.optional(types.array(types.string), []), // Multiple follower user IDs
  // Node status
  status: types.optional(types.maybeNull(types.string), null),
});

// Mindmap edge model
// Edges are computed from parent-child relationships, not stored as separate entities

// User presence model
const UserPresence = types.model('UserPresence', {
  userId: types.string,
  userName: types.string,
  userColor: types.string,
  lastSeen: types.number,
  cursor: types.maybeNull(types.model({
    x: types.number,
    y: types.number,
    timestamp: types.number,
  })),
});

// GitHub link model
const GitHubLink = types.model('GitHubLink', {
  id: types.string,
  nodeId: types.string,
  repositoryName: types.string,
  repositoryFullName: types.string,
  repositoryUrl: types.string,
  repositoryOwner: types.string,
  metadata: types.frozen(),
});

// Comment model
const NodeComment = types.model('NodeComment', {
  id: types.string,
  nodeId: types.string,
  userId: types.string,
  userName: types.optional(types.string, ''),
  text: types.string,
  createdAt: types.number,
  updatedAt: types.optional(types.number, 0),
  isEdited: types.optional(types.boolean, false),
});

// Reaction user model
const ReactionUser = types.model('ReactionUser', {
  id: types.string,
  email: types.string,
  name: types.string,
});

// Reaction group model (grouped by emoji)
const ReactionGroup = types.model('ReactionGroup', {
  emoji: types.string,
  count: types.number,
  users: types.array(ReactionUser),
});

// Reaction store for a single entity (comment or node)
const EntityReactions = types.model('EntityReactions', {
  entityId: types.identifier,
  entityType: types.enumeration(['comment', 'node']),
  reactions: types.array(ReactionGroup),
  lastUpdated: types.optional(types.number, 0),
});

// Helper function to extract plain text from BlockNote content
function extractPlainText(content: any[]): string {
  if (!Array.isArray(content)) return '';
  
  return content
    .map((block) => {
      if (block.content && Array.isArray(block.content)) {
        return block.content
          .map((item: any) => item.text || '')
          .join('');
      }
      return '';
    })
    .join('\n')
    .trim();
}

// Node Document model
const NodeDoc = types
  .model('NodeDoc', {
    id: types.identifier, // Unique document ID from backend
    nodeId: types.string,
    title: types.optional(types.string, ''),
    content: types.frozen(), // BlockNote JSON structure
    plainTextContent: types.optional(types.string, ''),
    version: types.optional(types.number, 1),
    sortOrder: types.optional(types.number, 0),
    lastSaved: types.optional(types.number, 0), // Unix timestamp
    createdAt: types.optional(types.number, () => Date.now()),
    updatedAt: types.optional(types.number, () => Date.now()),
    createdBy: types.optional(types.string, ''),
    
    // UI state (not persisted)
    saveState: types.optional(
      types.enumeration(['idle', 'dirty', 'saving', 'saved', 'error']), 
      'idle'
    ),
    saveError: types.maybeNull(types.string),
  })
  .views((self) => ({
    get isEmpty(): boolean {
      return (
        !self.content ||
        (Array.isArray(self.content) && self.content.length === 0) ||
        (Array.isArray(self.content) &&
          self.content.length === 1 &&
          self.content[0].type === 'paragraph' &&
          (!self.content[0].content || self.content[0].content.length === 0))
      );
    },
    
    get preview(): string {
      // Return first 100 chars of plain text for previews
      return (
        self.plainTextContent.substring(0, 100) +
        (self.plainTextContent.length > 100 ? '...' : '')
      );
    },
  }))
  .actions((self) => ({
    setContent(content: any) {
      console.log(`📝 [NodeDoc] setContent called for doc ${self.id}`);
      self.content = content;
      self.updatedAt = Date.now();
      self.saveState = 'dirty';
      
      // Extract plain text for search/preview
      self.plainTextContent = extractPlainText(content);
      console.log(`📝 [NodeDoc] Save state set to 'dirty'`);
    },
    
    setSaveState(state: 'idle' | 'dirty' | 'saving' | 'saved' | 'error') {
      self.saveState = state;
    },
    
    setSaveError(error: string | null) {
      self.saveError = error;
    },
    
    markSaved() {
      self.lastSaved = Date.now();
      self.saveState = 'saved';
      self.saveError = null;
    },
  }));

// Mindmap store to replace InstantDB queries
const MindmapStore = types
  .model('MindmapStore', {
    mindmapId: types.optional(types.string, 'default-mindmap'),
    nodes: types.array(MindmapNode),
    // Edges are computed from parent-child relationships, not stored
    users: types.array(UserPresence),
    githubLinks: types.array(GitHubLink),
    comments: types.array(NodeComment),
    reactions: types.array(EntityReactions),
    nodeDocs: types.map(types.array(NodeDoc)), // Map<nodeId, NodeDoc[]>
    version: types.optional(types.number, 0),
    isLoading: types.optional(types.boolean, false),
    isConnected: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    // UI State
    selectedNodeId: types.maybeNull(types.string),
    nodeDetailSidebarCollapsed: types.optional(types.boolean, true),
    // Cache repositories fetched from GitHub to avoid re-fetching on dialog reopen
    cachedRepositories: types.optional(types.frozen(), []),
    repositoriesCacheTimestamp: types.optional(types.number, 0),
  })
  .actions(self => ({
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    
    setConnected(connected: boolean) {
      self.isConnected = connected;
    },
    
    selectNode(nodeId: string | null) {
      console.log('🎯 MindmapStore: selectNode called with:', nodeId);
      self.selectedNodeId = nodeId;
    },
    
    setNodeDetailSidebarCollapsed(collapsed: boolean) {
      console.log('📊 MindmapStore: setNodeDetailSidebarCollapsed:', collapsed);
      self.nodeDetailSidebarCollapsed = collapsed;
    },
    
    openSidebar(nodeId: string) {
      console.log('🎯 MindmapStore: openSidebar called with:', nodeId);
      self.selectedNodeId = nodeId;
      self.nodeDetailSidebarCollapsed = false;
    },
    
    closeSidebar() {
      console.log('📊 MindmapStore: closeSidebar called');
      self.nodeDetailSidebarCollapsed = true;
    },
    
    toggleSidebar() {
      console.log('🔄 MindmapStore: toggleSidebar called');
      self.nodeDetailSidebarCollapsed = !self.nodeDetailSidebarCollapsed;
    },
    
    setError(error: string | null) {
      self.error = error;
    },
    
    setMindmapId(id: string) {
      self.mindmapId = id;
    },

    normalizeNodeId(nodeId: string) {
      return String(nodeId);
    },
    
    // Initialize mindmap data from WebSocket
    initializeData(data: { nodes: any[], edges: any[], version: number }) {
      console.log(`🔄 [MindmapStore] initializeData called:`, {
        incomingNodesCount: data.nodes?.length || 0,
        incomingEdgesCount: data.edges?.length || 0,
        version: data.version,
        currentNodesCount: self.nodes.length,
        mindmapId: self.mindmapId
      });
      
      self.nodes.clear();
      console.log(`🗑️ [MindmapStore] Cleared existing nodes`);
      
      data.nodes.forEach((node, index) => {
        // Extract label from data object if it exists
        const label = node.label || node.data?.label || '';
        
        console.log(`➕ [MindmapStore] Adding node ${index + 1}/${data.nodes.length}:`, {
          id: node.id,
          type: node.type,
          label,
          isRoot: node.isRoot || node.data?.isRoot
        });
        
        self.nodes.push({
          id: node.id,
          type: node.type || 'workflow',
          label: label,
          parentId: node.parentId === null ? undefined : node.parentId,  // Convert null to undefined
          isRoot: node.isRoot || node.data?.isRoot || false,
          priority: node.priority || node.data?.priority || 'medium',
          isNecessary: node.isNecessary || node.data?.isNecessary || false,
          links: node.links || node.data?.links || [],
          notes: node.notes || node.data?.notes || '',
          lead: node.lead || node.data?.lead || '',
        });
      });
      
      // Edges are computed from parent-child relationships, not stored
      
      self.version = data.version;
      self.isLoading = false;
      console.log(`✅ [MindmapStore] Mindmap initialized:`, {
        nodesCount: self.nodes.length,
        version: self.version,
        isLoading: self.isLoading
      });
    },
    
    // Apply WebSocket delta updates
    applyDelta(delta: any) {
      if (delta.addedNodes) {
        delta.addedNodes.forEach((node: any) => {
          self.nodes.push({
            id: node.id,
            type: node.type || 'workflow',
            label: node.label || '',
            parentId: node.parentId,
            isRoot: node.isRoot || false,
            priority: node.priority || 'medium',
          });
        });
      }
      
      if (delta.updatedNodes) {
        delta.updatedNodes.forEach((updated: any) => {
          const existing = self.nodes.find(n => n.id === updated.id);
          if (existing) {
            Object.assign(existing, updated);
          }
        });
      }
      
      if (delta.removedNodeIds) {
        delta.removedNodeIds.forEach((nodeId: string) => {
          const index = self.nodes.findIndex(n => n.id === nodeId);
          if (index >= 0) {
            self.nodes.splice(index, 1);
          }
        });
      }
      
      // Edges are computed from parent-child relationships, not stored
      
      self.version = delta.version;
      console.log(`🔄 Delta applied: v${delta.version}`);
    },
    
    // Update user presence
    updateUserPresence(users: any[]) {
      self.users.clear();
      users.forEach(user => {
        self.users.push({
          userId: user.userId,
          userName: user.userName,
          userColor: user.userColor,
          lastSeen: user.lastSeen,
          cursor: user.cursor,
        });
      });
    },
    
    // Optimistic node creation
    createNodeOptimistically: flow(function* (parentId: string | null) {
      const tempId = `temp-${uuidv4()}`;
      const newNode = {
        id: tempId,
        type: 'workflow',
        label: '', // Blank label so user can type immediately
        parentId: parentId,
        isRoot: !parentId,
        priority: 'medium',
        isNecessary: false,
        isOptimistic: true,
      };
      
      // Add node optimistically
      self.nodes.push(newNode);
      
      console.log('✅ [MindmapStore] Node created:', tempId, '→ parent:', parentId);
      
      // Track analytics
      analyticsService.trackNodeEvent('created', tempId, self.mindmapId || 'unknown', {
        parentId,
        nodeType: 'workflow',
        isOptimistic: true,
      });
      
      // Edges are computed from parent-child relationships
      
      try {
        // Auth is handled via httpOnly cookies
        
        // Send to backend (without position)
        const response = yield api.post(
          `/mindmaps/${self.mindmapId}/nodes`,
          {
            parentId: parentId,
            label: 'New Node',
            type: 'workflow',
            priority: 'medium',
            isNecessary: false,
          },
          { toastMessage: 'Node created' }
        );
        
        const createdNode = response.data;
        
        console.log('📡 [MindmapStore] Backend confirmed:', tempId, '→', createdNode.id);
        
        // Track successful creation
        analyticsService.trackNodeEvent('created_confirmed', createdNode.id, self.mindmapId || 'unknown', {
          parentId: createdNode.parentId,
          nodeType: createdNode.type,
        });
        
        // Replace optimistic node with real one
        const nodeIndex = self.nodes.findIndex(n => n.id === tempId);
        if (nodeIndex >= 0) {
          // Remove the old node first
          self.nodes.splice(nodeIndex, 1);
          
          // Add the new node with proper cloning using utility
          const safeNode = fromAPIToMST({
            id: createdNode.id,
            type: createdNode.type || 'workflow',
            label: createdNode.data?.label || 'New Node',
            parentId: createdNode.parentId || undefined,
            isRoot: createdNode.data?.isRoot || false,
            priority: createdNode.data?.priority || 'medium',
            isNecessary: createdNode.data?.isNecessary || false,
            isOptimistic: false,
          });
          self.nodes.push(safeNode);
        }
        
        // Edges are computed from parent-child relationships
        
        return { ...createdNode, tempId }; // Return both IDs for focusing
      } catch (error) {
        // Remove optimistic node on failure
        const nodeIndex = self.nodes.findIndex(n => n.id === tempId);
        if (nodeIndex >= 0) {
          self.nodes.splice(nodeIndex, 1);
        }
        
        // Edges are computed from parent-child relationships
        
        // Toast notification handled by interceptor
        self.setError('Failed to create node');
        throw error;
      }
    }),
    
    // Update node optimistically
    updateNodeOptimistically: flow(function* (nodeId: string, updates: any) {
      const node = self.nodes.find(n => n.id === nodeId);
      if (!node) return;
      
      // Store original values for rollback (deep clone)
      const originalValues = {
        label: node.label,
        type: node.type,
        priority: node.priority,
        isNecessary: node.isNecessary,
        parentId: node.parentId,
        isOptimistic: node.isOptimistic,
        links: node.links,
        notes: node.notes,
        lead: node.lead,
      };
      
      // Apply updates optimistically
      if (updates.data) {
        // Map data properties to node properties
        if (updates.data.label !== undefined) node.label = updates.data.label;
        if (updates.data.priority !== undefined) node.priority = updates.data.priority;
        if (updates.data.isNecessary !== undefined) node.isNecessary = updates.data.isNecessary;
        if (updates.data.links !== undefined) node.links = updates.data.links;
        if (updates.data.notes !== undefined) node.notes = updates.data.notes;
        if (updates.data.lead !== undefined) node.lead = updates.data.lead;
      }
      // Direct field updates
      if (updates.type !== undefined) node.type = updates.type;
      if (updates.parentId !== undefined) node.parentId = updates.parentId;
      if (updates.links !== undefined) node.links = updates.links;
      if (updates.notes !== undefined) node.notes = updates.notes;
      if (updates.lead !== undefined) node.lead = updates.lead;
      
      try {
        // Auth is handled via httpOnly cookies
        
        // Filter out position from updates before sending to backend
        const { position, ...backendUpdates } = updates;
        
        // Send to backend (without position)
        yield api.patch(
          `/mindmaps/nodes/${nodeId}`,
          backendUpdates,
          { toastMessage: 'Node updated' }
        );
        
      } catch (error) {
        // Rollback on failure
        node.label = originalValues.label;
        node.type = originalValues.type;
        node.priority = originalValues.priority;
        node.isNecessary = originalValues.isNecessary;
        node.parentId = originalValues.parentId;
        node.isOptimistic = originalValues.isOptimistic;
        node.links = originalValues.links;
        node.notes = originalValues.notes;
        node.lead = originalValues.lead;
        // Toast notification handled by interceptor
        self.setError('Failed to update node');
        throw error;
      }
    }),
    
    // Delete node optimistically
    deleteNodeOptimistically: flow(function* (nodeId: string, deleteChildren: boolean = false) {
      const nodeIndex = self.nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex < 0) return;
      
      // Store for rollback with deep cloning
      const node = self.nodes[nodeIndex];
      const deletedNode = {
        id: node.id,
        type: node.type,
        label: node.label,
        priority: node.priority,
        isNecessary: node.isNecessary,
        parentId: node.parentId,
        isOptimistic: node.isOptimistic,
      };
      
      let nodesToRemove: any[] = [deletedNode];
      let childUpdates: Array<{ childId: string; oldParentId: string | null; newParentId: string | null }> = [];
      
      if (deleteChildren) {
        // Delete node and all its descendants recursively
        const getAllDescendants = (parentId: string): any[] => {
          const children = self.nodes.filter(n => n.parentId === parentId);
          let descendants: any[] = [];
          children.forEach(child => {
            descendants.push({
              id: child.id,
              type: child.type,
              label: child.label,
              priority: child.priority,
              isNecessary: child.isNecessary,
              parentId: child.parentId,
              isOptimistic: child.isOptimistic,
            });
            descendants = descendants.concat(getAllDescendants(child.id));
          });
          return descendants;
        };
        
        nodesToRemove = [deletedNode, ...getAllDescendants(nodeId)];
        
        console.log(`🗑️ [MindmapStore] Deleting node and ${nodesToRemove.length - 1} descendants`);
        
        // Remove all nodes
        nodesToRemove.forEach(nodeToRemove => {
          const idx = self.nodes.findIndex(n => n.id === nodeToRemove.id);
          if (idx >= 0) {
            self.nodes.splice(idx, 1);
          }
        });
      } else {
        // Delete single node and reconnect children to parent
        const children = self.nodes.filter(n => n.parentId === nodeId);
        
        console.log(`🗑️ [MindmapStore] Deleting node, reconnecting ${children.length} children to parent: ${deletedNode.parentId}`);
        
        // Store child updates for rollback
        children.forEach(child => {
          childUpdates.push({
            childId: child.id,
            oldParentId: child.parentId,
            newParentId: deletedNode.parentId
          });
          child.parentId = deletedNode.parentId;
        });
        
        // Remove the node
        self.nodes.splice(nodeIndex, 1);
      }
      
      // Track analytics
      analyticsService.trackNodeEvent('deleted', nodeId, self.mindmapId || 'unknown', {
        nodeLabel: deletedNode.label,
        nodeType: deletedNode.type,
        deleteChildren,
        affectedNodes: nodesToRemove.length,
      });
      
      try {
        // Auth is handled via httpOnly cookies
        yield api.delete(
          `/mindmaps/${self.mindmapId}/nodes/${nodeId}?deleteChildren=${deleteChildren}`,
          { toastMessage: deleteChildren ? 'Node and children deleted' : 'Node deleted' }
        );
        
      } catch (error) {
        // Rollback on failure
        if (deleteChildren) {
          // Restore all deleted nodes
          nodesToRemove.forEach(nodeToRestore => {
            self.nodes.push(nodeToRestore);
          });
        } else {
          // Restore single node
          self.nodes.push(deletedNode);
          
          // Restore child parent relationships
          childUpdates.forEach(update => {
            const child = self.nodes.find(n => n.id === update.childId);
            if (child) {
              child.parentId = update.oldParentId;
            }
          });
        }
        
        // Toast notification handled by interceptor
        self.setError('Failed to delete node');
        throw error;
      }
    }),
    
    // Re-parent a node by changing its parentId
    reparentNodeOptimistically: flow(function* (nodeId: string, newParentId: string | null) {
      const node = self.nodes.find(n => n.id === nodeId);
      if (!node) {
        console.error(`❌ [MindmapStore] Cannot reparent - node not found: ${nodeId}`);
        return;
      }
      
      // Prevent circular dependencies
      if (newParentId) {
        const wouldCreateCycle = (checkParentId: string | null | undefined): boolean => {
          if (!checkParentId) return false;
          if (checkParentId === nodeId) return true;
          const parent = self.nodes.find(n => n.id === checkParentId);
          return parent ? wouldCreateCycle(parent.parentId) : false;
        };
        
        if (wouldCreateCycle(newParentId)) {
          console.error(`❌ [MindmapStore] Cannot reparent - would create circular dependency`);
          ToastService.showToast('Cannot create circular parent-child relationship', 'warning');
          return;
        }
      }
      
      // Store original for rollback
      const originalParentId = node.parentId;
      
      console.log(`🔄 [MindmapStore] Reparenting node ${nodeId}: ${originalParentId} → ${newParentId}`);
      
      // Update optimistically
      node.parentId = newParentId;
      
      // Track analytics
      analyticsService.trackNodeEvent('reparented', nodeId, self.mindmapId || 'unknown', {
        oldParentId: originalParentId,
        newParentId,
        nodeLabel: node.label,
      });
      
      try {
        // Send update to backend
        yield api.patch(
          `/mindmaps/nodes/${nodeId}`,
          { parentId: newParentId },
          { toastMessage: 'Node re-parented successfully' }
        );
        
        console.log(`✅ [MindmapStore] Node reparented successfully`);
      } catch (error) {
        // Rollback on failure
        console.error(`❌ [MindmapStore] Failed to reparent node:`, error);
        node.parentId = originalParentId;
        self.setError('Failed to re-parent node');
        throw error;
      }
    }),
    // Add comment optimistically
    addCommentOptimistically: flow(function* (nodeId: string, text: string, mentions?: string[]) {
      const tempId = `temp-comment-${uuidv4()}`;
      const node = self.nodes.find(n => n.id === nodeId);
      if (!node) return;
      
      // Add to node's comment count optimistically
      if (node.data) {
        node.data.commentCount = (node.data.commentCount || 0) + 1;
      }
      
      try {
        // Auth is handled via httpOnly cookies
        
        const response = yield api.post(
          `/mindmaps/nodes/${nodeId}/comments`,
          { text, mentions },
          { toastMessage: 'Comment added' }
        );
        
        // Comment created successfully
        return response.data;
      } catch (error) {
        // Rollback comment count
        if (node.data) {
          node.data.commentCount = Math.max(0, (node.data.commentCount || 1) - 1);
        }
        throw error;
      }
    }),
    
    // Link repository optimistically
    linkRepositoryOptimistically: flow(function* (nodeId: string, repoUrl: string) {
      const node = self.nodes.find(n => n.id === nodeId);
      if (!node) return;
      
      // Store original repos for rollback
      const originalRepos = node.data?.linkedRepositories ? [...node.data.linkedRepositories] : [];
      
      // Add repo optimistically
      if (!node.data) node.data = {};
      if (!node.data.linkedRepositories) node.data.linkedRepositories = [];
      node.data.linkedRepositories.push({
        id: `temp-repo-${uuidv4()}`,
        url: repoUrl,
        name: repoUrl.split('/').pop(),
        isOptimistic: true
      });
      
      try {
        // Auth is handled via httpOnly cookies
        
        const response = yield api.post(
          `/mindmaps/nodes/${nodeId}/repositories`,
          { url: repoUrl },
          { toastMessage: 'Repository linked' }
        );
        
        // Replace optimistic repo with real one
        const tempRepo = node.data.linkedRepositories.find(r => r.isOptimistic);
        if (tempRepo) {
          const index = node.data.linkedRepositories.indexOf(tempRepo);
          node.data.linkedRepositories[index] = fromAPIToMST(response.data);
        }
        
        return response.data;
      } catch (error) {
        // Rollback to original repos
        if (node.data) {
          node.data.linkedRepositories = originalRepos;
        }
        throw error;
      }
    }),
    
    // Reset store to initial state
    reset() {
      console.log('🔄 [MindmapStore] Resetting...');
      self.mindmapId = 'default-mindmap';
      self.nodes.clear();
      self.users.clear();
      self.version = 0;
      self.isLoading = false;
      self.isConnected = false;
      self.error = undefined;
    },
    
    // WebSocket patch handling with conflict resolution
    handleWebSocketPatch: flow(function* (patch: any) {
      console.log('[MindmapStore] Handling WebSocket patch:', patch);
      
      // Extract type from event (handle both 'node_created' and 'mindmap:node:created' formats)
      const eventType = patch.type?.replace('mindmap:', '') || patch.type;
      
      // Handle different patch types
      switch (eventType) {
        case 'node_created':
        case 'node:created':
          // Extract node from patch (backend sends patch.data.node)
          const nodeData = patch.data?.node || patch.data;
          
          // Don't apply if we have an optimistic version
          const existingOptimistic = self.nodes.find(n => 
            n.isOptimistic && n.parentId === nodeData.parentId
          );
          
          if (existingOptimistic) {
            console.log('[MindmapStore] Skipping patch - have optimistic version');
            return;
          }
          
          // Flatten nested data structure: backend sends node.data.{label, priority}
          // but MST expects flat {label, priority}
          const newNode = fromAPIToMST({
            id: nodeData.id,
            type: nodeData.type || 'workflow',
            label: nodeData.data?.label || nodeData.label || 'New Node',
            parentId: nodeData.parentId || undefined,
            isRoot: nodeData.data?.isRoot ?? nodeData.isRoot ?? false,
            priority: nodeData.data?.priority || nodeData.priority || 'medium',
            isNecessary: nodeData.data?.isNecessary ?? nodeData.isNecessary ?? false,
            isOptimistic: false,
          });
          console.log('[MindmapStore] Converted node for MST:', newNode);
          self.nodes.push(newNode);
          break;
          
        case 'node_updated':
        case 'node:updated':
          // Backend sends nested structure: {data: {node: {id, label, ...}, mindmapId}}
          const updatedNodeData = patch.data.node || patch.data;
          const nodeId = updatedNodeData.id || patch.entityId;
          
          console.log('[MindmapStore] 🔍 NODE UPDATE - Looking for node:', nodeId);
          console.log('[MindmapStore] 🔍 Update data:', updatedNodeData);
          
          const nodeToUpdate = self.nodes.find(n => n.id === nodeId);
          console.log('[MindmapStore] 🔍 Node found?', !!nodeToUpdate);
          
          if (nodeToUpdate) {
            console.log('[MindmapStore] 🔍 Node isOptimistic?', nodeToUpdate.isOptimistic);
            // Don't overwrite if this node is currently being optimistically updated
            if (nodeToUpdate.isOptimistic) {
              console.log('[MindmapStore] Skipping update - node is optimistically updated');
              return;
            }
            
            // Backend sends nested data: node.data.{label, priority, status, etc.}
            // But we need to extract from either updatedNodeData.data OR updatedNodeData directly
            const updates = updatedNodeData.data || updatedNodeData;
            
            // Apply updates individually to trigger MobX reactivity
            if (updates.label !== undefined) nodeToUpdate.label = updates.label;
            if (updates.priority !== undefined) nodeToUpdate.priority = updates.priority;
            if (updates.isNecessary !== undefined) nodeToUpdate.isNecessary = updates.isNecessary;
            if (updates.status !== undefined) nodeToUpdate.status = updates.status;
            if (updates.lead !== undefined) nodeToUpdate.lead = updates.lead;
            if (updates.notes !== undefined) nodeToUpdate.notes = updates.notes;
            if (updates.links !== undefined) nodeToUpdate.links = updates.links;
            if (updates.isRoot !== undefined) nodeToUpdate.isRoot = updates.isRoot;
            
            // Handle parentId from top level (not in data)
            if (updatedNodeData.parentId !== undefined) nodeToUpdate.parentId = updatedNodeData.parentId;
            
            console.log('[MindmapStore] ✅ Updated node reactively:', nodeToUpdate.id, {
              status: nodeToUpdate.status,
              lead: nodeToUpdate.lead,
              label: nodeToUpdate.label,
              priority: nodeToUpdate.priority
            });
          } else {
            console.warn('[MindmapStore] ⚠️ Node not found for update:', nodeId);
          }
          break;
          
        case 'node_deleted':
        case 'node:deleted':
          const nodeIndex = self.nodes.findIndex(n => n.id === patch.data.id);
          if (nodeIndex >= 0) {
            // Don't delete if this node is optimistically created
            if (self.nodes[nodeIndex].isOptimistic) {
              console.log('[MindmapStore] Skipping delete - node is optimistically created');
              return;
            }
            self.nodes.splice(nodeIndex, 1);
          }
          break;
          
        default:
          console.warn('[MindmapStore] Unknown patch type:', eventType, '(original:', patch.type, ')');
      }
    }),
    
    // Resolve optimistic updates when confirmed by server
    resolveOptimisticUpdate(tempId: string, realId: string) {
      const node = self.nodes.find(n => n.id === tempId);
      if (node) {
        node.id = realId;
        node.isOptimistic = false;
        console.log('[MindmapStore] Resolved optimistic update:', tempId, '→', realId);
      }
    },
    
    // Load GitHub links for the entire mindmap
    loadGithubLinks: flow(function* () {
      if (!self.mindmapId) return;
      
      try {
        console.log(`🔗 [MindmapStore] Loading GitHub links for mindmap: ${self.mindmapId}`);
        const response = yield api.get(`/github/mindmaps/${self.mindmapId}/links`, {
          withCredentials: true
        });
        
        if (response.data && Array.isArray(response.data)) {
          self.githubLinks.clear();
          response.data.forEach((link: any) => {
            self.githubLinks.push({
              id: link.id,
              nodeId: link.nodeId,
              repositoryName: link.repositoryName,
              repositoryFullName: link.repositoryFullName,
              repositoryUrl: link.repositoryUrl,
              repositoryOwner: link.repositoryOwner,
              metadata: link.metadata || {},
            });
          });
          console.log(`✅ [MindmapStore] Loaded ${self.githubLinks.length} GitHub links`);
        }
      } catch (error) {
        console.error('[MindmapStore] Error loading GitHub links:', error);
      }
    }),
    
    // Link a repository to a node
    linkRepositoryToNode: flow(function* (nodeId: string, repository: any) {
      if (!self.mindmapId) {
        console.error('[MindmapStore] Cannot link repo: no mindmap ID');
        return { success: false, error: 'No mindmap ID' };
      }
      
      try {
        console.log(`🔗 [MindmapStore] Linking repo ${repository.fullName} to node ${nodeId}`);
        
        const response = yield api.post(`/github/nodes/${nodeId}/link`, {
          mindmapId: self.mindmapId,
          repositoryUrl: repository.url || repository.html_url,
          repositoryOwner: repository.owner,
          repositoryName: repository.name,
          repositoryFullName: repository.fullName || repository.full_name,
          metadata: {
            description: repository.description,
            language: repository.language,
            stars: repository.stars || repository.stargazers_count,
            forks: repository.forks || repository.forks_count,
            isPrivate: repository.isPrivate || repository.private,
          },
        }, {
          withCredentials: true
        });
        
        // Add to local store
        if (response.data) {
          self.githubLinks.push({
            id: response.data.id,
            nodeId: nodeId,
            repositoryName: repository.name,
            repositoryFullName: repository.fullName || repository.full_name,
            repositoryUrl: repository.url || repository.html_url,
            repositoryOwner: repository.owner,
            metadata: response.data.metadata || {},
          });
          console.log(`✅ [MindmapStore] Repository linked successfully`);
        }
        
        return { success: true, data: response.data };
      } catch (error: any) {
        console.error('[MindmapStore] Error linking repository:', error);
        return { success: false, error: error.message || 'Failed to link repository' };
      }
    }),
    
    // Get GitHub links for a specific node
    getNodeGithubLinks(nodeId: string) {
      return self.githubLinks.filter(link => link.nodeId === nodeId);
    },
    
    // Cache repositories to avoid re-fetching
    setCachedRepositories(repositories: any[]) {
      console.log(`💾 [MindmapStore] Caching ${repositories.length} repositories`);
      self.cachedRepositories = repositories;
      self.repositoriesCacheTimestamp = Date.now();
    },
    
    // Get cached repositories if they're fresh (less than 5 minutes old)
    getCachedRepositories() {
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      const age = now - self.repositoriesCacheTimestamp;
      
      if (self.cachedRepositories.length > 0 && age < CACHE_TTL) {
        console.log(`✅ [MindmapStore] Returning cached repositories (age: ${Math.round(age / 1000)}s)`);
        return self.cachedRepositories;
      }
      
      console.log(`🔄 [MindmapStore] Cache expired or empty, will fetch fresh data`);
      return null;
    },
    
    // Remove a GitHub link from the store
    removeGithubLink: flow(function* (linkId: string) {
      try {
        console.log(`🗑️ [MindmapStore] Removing GitHub link: ${linkId}`);
        
        // Remove from backend
        yield api.delete(`/github/links/${linkId}`, {
          withCredentials: true
        });
        
        // Remove from local store
        const index = self.githubLinks.findIndex(link => link.id === linkId);
        if (index >= 0) {
          self.githubLinks.splice(index, 1);
          console.log(`✅ [MindmapStore] GitHub link removed successfully`);
        }
        
        return { success: true };
      } catch (error: any) {
        console.error('[MindmapStore] Error removing GitHub link:', error);
        return { success: false, error: error.message || 'Failed to remove link' };
      }
    }),
    
    // Load comments for a specific node
    loadNodeComments: flow(function* (nodeId: string) {
      try {
        console.log(`💬 [MindmapStore] Loading comments for node: ${nodeId}`);
        const response = yield api.get(`/mindmaps/nodes/${nodeId}/comments`, {
          withCredentials: true
        });
        
        if (response.data && Array.isArray(response.data)) {
          // Remove existing comments for this node
          const existingIndices = [];
          self.comments.forEach((comment, index) => {
            if (comment.nodeId === nodeId) {
              existingIndices.push(index);
            }
          });
          existingIndices.reverse().forEach(index => self.comments.splice(index, 1));
          
          // Add new comments
          response.data.forEach((comment: any) => {
            self.comments.push({
              id: comment.id,
              nodeId: comment.nodeId,
              userId: comment.userId,
              userName: comment.userName || '',
              text: comment.text,
              createdAt: Number(comment.createdAt),
              updatedAt: Number(comment.updatedAt) || 0,
              isEdited: comment.isEdited || false,
            });
          });
          console.log(`✅ [MindmapStore] Loaded ${response.data.length} comments for node ${nodeId}`);
        }
      } catch (error) {
        console.error('[MindmapStore] Error loading comments:', error);
      }
    }),
    
    // Create a comment on a node
    createComment: flow(function* (nodeId: string, text: string) {
      try {
        console.log(`💬 [MindmapStore] Creating comment on node: ${nodeId}`);
        const response = yield api.post(`/mindmaps/nodes/${nodeId}/comments`, {
          text
        }, {
          withCredentials: true
        });
        
        if (response.data) {
          self.comments.push({
            id: response.data.id,
            nodeId: response.data.nodeId,
            userId: response.data.userId,
            userName: response.data.userName || 'Anonymous',
            text: response.data.text,
            createdAt: Number(response.data.createdAt),
            updatedAt: Number(response.data.updatedAt) || 0,
            isEdited: false,
          });
          console.log(`✅ [MindmapStore] Comment created successfully`);
        }
        
        return { success: true, data: response.data };
      } catch (error: any) {
        console.error('[MindmapStore] Error creating comment:', error);
        return { success: false, error: error.message || 'Failed to create comment' };
      }
    }),
    
    // Get comments for a specific node
    getNodeComments(nodeId: string) {
      return self.comments.filter(comment => comment.nodeId === nodeId);
    },

    // ===== Reaction Actions =====

    /**
     * Load reactions for a specific entity (comment or node)
     */
    loadReactions: flow(function* (entityType: 'comment' | 'node', entityId: string) {
      try {
        console.log(`😀 [MindmapStore] Loading reactions for ${entityType}: ${entityId}`);
        
        const endpoint = entityType === 'comment'
          ? `/mindmaps/comments/${entityId}/reactions`
          : `/mindmaps/nodes/${entityId}/reactions`;

        const response = yield api.get(endpoint, {
          withCredentials: true
        });

        // Find or create entity reactions entry
        let entityReactions = self.reactions.find(
          r => r.entityId === entityId && r.entityType === entityType
        );

        if (!entityReactions) {
          self.reactions.push({
            entityId,
            entityType,
            reactions: [],
            lastUpdated: Date.now(),
          });
          entityReactions = self.reactions.find(
            r => r.entityId === entityId && r.entityType === entityType
          );
        }

        // Update reactions
        if (entityReactions) {
          entityReactions.reactions.clear();
          (response.data || []).forEach((reactionGroup: any) => {
            entityReactions.reactions.push({
              emoji: reactionGroup.emoji,
              count: reactionGroup.count,
              users: reactionGroup.users.map((user: any) => ({
                id: user.id,
                email: user.email,
                name: user.name || user.email,
              })),
            });
          });
          entityReactions.lastUpdated = Date.now();
        }

        console.log(`✅ [MindmapStore] Reactions loaded: ${response.data?.length || 0}`);
        return { success: true, data: response.data };
      } catch (error: any) {
        console.error('[MindmapStore] Error loading reactions:', error);
        return { success: false, error: error.message || 'Failed to load reactions' };
      }
    }),

    /**
     * Add a reaction to an entity
     */
    addReaction: flow(function* (entityType: 'comment' | 'node', entityId: string, emoji: string) {
      try {
        console.log(`😀 [MindmapStore] Adding reaction ${emoji} to ${entityType}: ${entityId}`);
        
        const endpoint = entityType === 'comment'
          ? `/mindmaps/comments/${entityId}/reactions`
          : `/mindmaps/nodes/${entityId}/reactions`;

        const response = yield api.post(endpoint, { emoji }, {
          withCredentials: true
        });

        if (response.data.success) {
          // Reload reactions to get updated state
          const loadAction = (self as any).loadReactions;
          if (loadAction) {
            yield loadAction(entityType, entityId);
          }
          console.log(`✅ [MindmapStore] Reaction added successfully`);
        }

        return { success: true, data: response.data };
      } catch (error: any) {
        console.error('[MindmapStore] Error adding reaction:', error);
        return { success: false, error: error.message || 'Failed to add reaction' };
      }
    }),

    /**
     * Remove a reaction from an entity
     */
    removeReaction: flow(function* (entityType: 'comment' | 'node', entityId: string, emoji: string) {
      try {
        console.log(`😀 [MindmapStore] Removing reaction ${emoji} from ${entityType}: ${entityId}`);
        
        const endpoint = entityType === 'comment'
          ? `/mindmaps/comments/${entityId}/reactions/${encodeURIComponent(emoji)}`
          : `/mindmaps/nodes/${entityId}/reactions/${encodeURIComponent(emoji)}`;

        const response = yield api.delete(endpoint, {
          withCredentials: true
        });

        if (response.data.success) {
          // Reload reactions to get updated state
          const loadAction = (self as any).loadReactions;
          if (loadAction) {
            yield loadAction(entityType, entityId);
          }
          console.log(`✅ [MindmapStore] Reaction removed successfully`);
        }

        return { success: true, data: response.data };
      } catch (error: any) {
        console.error('[MindmapStore] Error removing reaction:', error);
        return { success: false, error: error.message || 'Failed to remove reaction' };
      }
    }),

    /**
     * Get reactions for a specific entity
     */
    getEntityReactions(entityType: 'comment' | 'node', entityId: string) {
      const entityReactions = self.reactions.find(
        r => r.entityId === entityId && r.entityType === entityType
      );
      return entityReactions?.reactions || [];
    },

    /**
     * Create a new document for a node
     */
    createNodeDoc: flow(function* (
      nodeId: string,
      { title }: { title?: string } = {}
    ) {
      try {
        const safeNodeId = String(nodeId);
        const existingDocs = self.nodeDocs.get(safeNodeId) || [];

        const newDoc = NodeDoc.create({
          id: `temp-${uuidv4()}`,
          nodeId: safeNodeId,
          title: title || '',
          content: [
            {
              type: 'paragraph',
              content: [],
            },
          ],
          plainTextContent: '',
          createdAt: Date.now(),
          updatedAt: Date.now(),
          sortOrder:
            existingDocs.length > 0
              ? Math.max(...existingDocs.map((doc) => doc.sortOrder)) + 10
              : 0,
        });

        self.nodeDocs.set(safeNodeId, [...existingDocs, newDoc]);

        // Optimistically save to backend
        const savedDoc = yield self.saveNodeDoc(nodeId, newDoc.id, {
          insertAtEnd: true,
          title,
        });

        if (savedDoc) {
          const docs = self.nodeDocs.get(safeNodeId) || [];
          self.nodeDocs.set(
            safeNodeId,
            docs.map(doc => (doc.id === newDoc.id ? savedDoc : doc))
          );
        }

        // Track analytics if available
        if (analyticsService?.trackEvent) {
          analyticsService.trackEvent('node_doc_created', {
            nodeId,
            documentId: savedDoc?.id || newDoc.id,
          });
        }

        return savedDoc || newDoc;
      } catch (error) {
        console.error('Error creating node doc:', error);
        ToastService.error('Failed to create document');
        throw error;
      }
    }),
    
    /**
     * Update node document content (triggers debounced save)
     */
    updateNodeDocContent(nodeId: string, documentId: string, content: any) {
      const safeNodeId = String(nodeId);
      const docs = self.nodeDocs.get(safeNodeId);
      if (!docs) {
        console.warn('No docs found for node:', safeNodeId);
        return;
      }

      const doc = docs.find((d) => d.id === documentId);
      if (!doc) {
        console.warn('No doc found for node:', safeNodeId, 'doc:', documentId);
        return;
      }

      console.log(`💾 [MindmapStore] updateNodeDocContent called for doc ${documentId}`);
      doc.setContent(content);
      
      // Debounce the save operation using a closure variable
      // This avoids the frozen object issue
      if (!(self as any)._saveTimeouts) {
        (self as any)._saveTimeouts = new Map();
      }

      const timeouts = (self as any)._saveTimeouts as Map<string, NodeJS.Timeout>;
      const key = `${nodeId}:${documentId}`;

      if (timeouts.has(key)) {
        clearTimeout(timeouts.get(key)!);
        console.log(`⏱️  [MindmapStore] Clearing existing timeout for ${key}`);
      }

      console.log(`⏱️  [MindmapStore] Setting 2s debounce timer for ${key}`);
      const timeoutId = setTimeout(() => {
        console.log(`💾 [MindmapStore] Debounce timer fired, saving ${key}`);
        self.saveNodeDoc(nodeId, documentId);
        timeouts.delete(key);
      }, 2000);

      timeouts.set(key, timeoutId);
    },
    
    /**
     * Save node document to backend
     */
    saveNodeDoc: flow(function* (
      nodeId: string,
      documentId: string,
      options?: { title?: string; insertAtEnd?: boolean }
    ) {
      const safeNodeId = String(nodeId);
      const docs = self.nodeDocs.get(safeNodeId);
      if (!docs) {
        console.warn('No docs to save for node:', safeNodeId);
        return null;
      }

      const doc = docs.find((d) => d.id === documentId);
      if (!doc) {
        console.warn('No doc to save for node:', safeNodeId, 'documentId:', documentId);
        return null;
      }

      try {
        doc.setSaveState('saving');

        const response = yield api.put(`/nodes/${safeNodeId}/doc`, {
          content: doc.content,
          plainTextContent: doc.plainTextContent,
          version: doc.version,
          documentId: doc.id.startsWith('temp-') ? undefined : doc.id,
          title: options?.title ?? doc.title,
          sortOrder: doc.sortOrder,
          insertAtEnd: options?.insertAtEnd,
        });

        const data = response.data;
        doc.markSaved();
        doc.version = data.version || doc.version;
        doc.lastSaved = Date.now();
        doc.sortOrder = data.sortOrder ?? doc.sortOrder;
        doc.title = data.title || doc.title;
        doc.updatedAt = new Date(data.updatedAt).getTime();
        doc.createdAt = new Date(data.createdAt).getTime();

        if (analyticsService?.trackEvent) {
          analyticsService.trackEvent('node_doc_saved', {
            nodeId,
            documentId: doc.id,
            contentLength: doc.plainTextContent.length,
          });
        }

        const savedDoc = NodeDoc.create({
          id: data.id,
          nodeId: data.nodeId,
          title: data.title || '',
          content: data.content,
          plainTextContent: data.plainTextContent || '',
          version: data.version || 1,
          sortOrder: data.sortOrder ?? doc.sortOrder,
          createdAt: new Date(data.createdAt).getTime(),
          updatedAt: new Date(data.updatedAt).getTime(),
          createdBy: data.createdBy || doc.createdBy,
          lastSaved: Date.now(),
        });

        // If the ID changed from temp to real, update the store
        if (doc.id !== savedDoc.id) {
          console.log(`📝 [MindmapStore] Updating doc ID from ${doc.id} to ${savedDoc.id}`);
          const existingDocs = self.nodeDocs.get(safeNodeId) || [];
          const updatedDocs = existingDocs.map(d => d.id === doc.id ? savedDoc : d);
          self.nodeDocs.set(safeNodeId, updatedDocs);
        }

        return savedDoc;
      } catch (error: any) {
        console.error('Error saving node doc:', error);
        doc.setSaveState('error');
        doc.setSaveError(error.message || 'Failed to save');
        ToastService.error('Failed to save document. Retrying...');

        setTimeout(() => {
          self.saveNodeDoc(nodeId, documentId, options);
        }, 5000);

        return null;
      }
    }),
    
    /**
     * Load node document from backend
     */
    loadNodeDocs: flow(function* (nodeId: string) {
      try {
        const response = yield api.get(`/nodes/${nodeId}/doc`);

        if (Array.isArray(response.data)) {
          const docs = response.data.map((doc: any) =>
            NodeDoc.create({
              id: String(doc.id),
              nodeId: String(doc.nodeId),
              title: doc.title || '',
              content: doc.content,
              plainTextContent: doc.plainTextContent || '',
              version: doc.version || 1,
              sortOrder: doc.sortOrder ?? 0,
              lastSaved: Date.now(),
              createdAt: new Date(doc.createdAt).getTime(),
              updatedAt: new Date(doc.updatedAt).getTime(),
              createdBy: doc.createdBy || '',
            })
          );

          self.nodeDocs.set(String(nodeId), docs);
          return docs;
        }

        return [];
      } catch (error: any) {
        if (error.response?.status === 404) {
          self.nodeDocs.set(String(nodeId), []);
          return [];
        }
        console.error('Error loading node docs:', error);
        ToastService.error('Failed to load documents');
        throw error;
      }
    }),
    
    /**
     * Delete node document
     */
    deleteNodeDoc: flow(function* (nodeId: string, documentId: string) {
      try {
        const safeNodeId = String(nodeId);
        const docs = self.nodeDocs.get(safeNodeId);
        if (!docs) return;

        yield api.delete(`/nodes/${safeNodeId}/doc/${documentId}`);

        self.nodeDocs.set(
          safeNodeId,
          docs.filter((doc) => doc.id !== documentId)
        );

        ToastService.success('Document deleted');

        if (analyticsService?.trackEvent) {
          analyticsService.trackEvent('node_doc_deleted', {
            nodeId,
            documentId,
          });
        }
      } catch (error) {
        console.error('Error deleting node doc:', error);
        ToastService.error('Failed to delete document');
        throw error;
      }
    }),
  }))
  .views(self => ({
    get reactFlowNodes(): Node[] {
      return self.nodes.map((node, index) => {
        // Count metadata for this node
        const commentCount = self.comments.filter(c => c.nodeId === node.id).length;
        const repositoryCount = self.githubLinks.filter(link => link.nodeId === node.id).length;
        // Count external links from node.links array
        const linkCount = Array.isArray(node.links) ? node.links.length : 0;
        // Check if node has notes/description
        const hasDescription = !!(node.notes && node.notes.trim().length > 0);
        // Check if node has documents and count them
        const documentCount = self.getNodeDocs(node.id).filter(doc => !doc.isEmpty).length;
        
        return {
          id: node.id,
          type: node.type,
          // Give nodes an initial spread position to avoid overlap at origin
          position: { 
            x: node.parentId ? 250 : 100, 
            y: index * 100 
          },
          data: {
            label: node.label,
            parentId: node.parentId,
            isRoot: node.isRoot,
            priority: node.priority,
            isNecessary: node.isNecessary,
            isOptimistic: node.isOptimistic,
            lead: node.lead,
            links: node.links,
            notes: node.notes,
            // Metadata counts for indicators
            commentCount,
            repositoryCount,
            linkCount,
            hasDescription,
            documentCount,
          },
        };
      });
    },
    
    get reactFlowEdges(): Edge[] {
      // Compute edges from parent-child relationships
      const edges: Edge[] = [];
      self.nodes.forEach(node => {
        if (node.parentId) {
          // Check if parent exists
          const parentExists = self.nodes.some(n => n.id === node.parentId);
          if (parentExists) {
            edges.push({
              id: `edge-${node.parentId}-${node.id}`,
              source: node.parentId,
              target: node.id,
              type: 'workflow', // Always use workflow type for consistency
              animated: false,
            });
          }
        }
      });
      return edges;
    },
    
    get activeUsers() {
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      return self.users.filter(user => user.lastSeen > fiveMinutesAgo);
    },

    /**
     * Get a node document by node ID
     */
    getNodeDocs(nodeId: string): Instance<typeof NodeDoc>[] {
      return self.nodeDocs.get(nodeId) || [];
    },

    getNodeDoc(nodeId: string, documentId?: string) {
      const docs = self.nodeDocs.get(nodeId) || [];
      if (!documentId) {
        return docs[0];
      }
      return docs.find((doc) => doc.id === documentId);
    },

    hasNodeDoc(nodeId: string): boolean {
      const docs = self.nodeDocs.get(nodeId);
      return !!docs && docs.some((doc) => !doc.isEmpty);
    },
  }));

export type IMindmapStore = Instance<typeof MindmapStore>;
export { MindmapStore };
